import { useEffect, useState } from 'react'
import { contactArchiveApi, type ArchiveDocument, type ContactArchive } from '../api/contactOverview'
import { Callout, Card, CardContent, CardHeader, IconButtonLink, List, ListItem, LoadingState, StatusPill } from './ui'
import { documentsApi } from '../api/documents'

/**
 * F-156 (TASK-0410, REQ-022-001): archivio del cliente ad albero, dietro il flag `clienti.archivio_albero`
 * (la pagina lo mostra solo se acceso). Cliente → Documenti del cliente → Pratiche → Allegati → Comunicazioni.
 * Solo elenchi: nessun contenuto di file, nessun percorso, nessun importo. Il file si apre da
 * `/api/documents/{id}/content`, dove vale il gate dei documenti. Email, SMS e WhatsApp non ci sono ancora (E2).
 * Componenti M3.
 */
const ROME = 'Europe/Rome'

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function DocumentRow({ d }: { d: ArchiveDocument }) {
  return (
    <ListItem
      headline={
        <>
          {d.name}
          {d.type ? ` · ${d.type}` : ''}
          {d.generated ? ' · generato' : ''}
        </>
      }
      supporting={formatRome(d.createdAt)}
      trailing={
        <>
          {d.uploadStatus !== 'Completato' && <StatusPill tone="warning">{d.uploadStatus}</StatusPill>}
          <IconButtonLink
            href={documentsApi.contentUrl(d.id)}
            icon="open_in_new"
            size="sm"
            aria-label={`Apri ${d.name}`}
          />
        </>
      }
    />
  )
}

export function ContactArchiveTree({ contactId }: { contactId: number }) {
  const [data, setData] = useState<ContactArchive | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!Number.isFinite(contactId)) return
    let alive = true
    setLoading(true)
    setError('')
    contactArchiveApi.get(contactId)
      .then(r => { if (alive) setData(r) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : "Errore nel caricamento dell'archivio") })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [contactId])

  return (
    <Card variant="outlined">
      <CardHeader title="Archivio del cliente" subtitle={data?.contact.name} />
      <CardContent>
        <div className="md-stack">
          {loading && <LoadingState />}
          {error && <Callout tone="error">{error}</Callout>}
          {data && (
            <>
              <section className="d-flex flex-column gap-2">
                <h3 className="md-typescale-title-small m-0">Documenti del cliente ({data.customerDocuments.length})</h3>
                {data.customerDocuments.length === 0 ? (
                  <div className="text-muted">Nessun documento.</div>
                ) : (
                  <List aria-label="Documenti del cliente">
                    {data.customerDocuments.map(d => <DocumentRow key={d.id} d={d} />)}
                  </List>
                )}
              </section>

              <section className="d-flex flex-column gap-2">
                <h3 className="md-typescale-title-small m-0">Pratiche ({data.contracts.length})</h3>
                {data.contracts.length === 0 && <div className="text-muted">Nessuna pratica visibile.</div>}
                {data.contracts.map(k => (
                  <Card key={k.id} variant="filled" padding="sm">
                    <div className="md-cluster">
                      <span className="md-typescale-emphasized-body-medium">{k.referenceCode}</span>
                      {k.departmentName ? <span>· {k.departmentName}</span> : null}
                      <span>· {new Date(k.signedAt ?? k.createdAt).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })}</span>
                      <StatusPill tone="neutral">{k.status}</StatusPill>
                    </div>
                    {k.documents.length === 0 ? (
                      <div className="text-muted">Nessun allegato.</div>
                    ) : (
                      <List aria-label={`Allegati della pratica ${k.referenceCode}`}>
                        {k.documents.map(d => <DocumentRow key={d.id} d={d} />)}
                      </List>
                    )}
                  </Card>
                ))}
              </section>

              <section className="d-flex flex-column gap-2">
                <h3 className="md-typescale-title-small m-0">Comunicazioni</h3>
                {data.communications.calls.length === 0 && data.communications.appointments.length === 0 ? (
                  <div className="text-muted">Nessuna chiamata né appuntamento registrati.</div>
                ) : (
                  <List aria-label="Comunicazioni">
                    {data.communications.calls.map(c => (
                      <ListItem
                        key={`c-${c.id}`}
                        headline={`Chiamata · ${formatRome(c.occurredAt)} · ${c.outcome ?? 'esito non indicato'}${c.operatorName ? ` · ${c.operatorName}` : ''}`}
                      />
                    ))}
                    {data.communications.appointments.map(a => (
                      <ListItem key={`a-${a.id}`} headline={`Appuntamento · ${formatRome(a.startAt)} · ${a.status}`} />
                    ))}
                  </List>
                )}
                {data.communications.truncated && <div className="text-muted">Elenco chiamate troncato alle più recenti.</div>}
                <div className="md-typescale-body-small text-muted">
                  {data.communications.note} (canali mancanti: {data.communications.missingChannels.join(', ')})
                </div>
              </section>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
